import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import PromptModal from "./PromptModal";
import AiAnalysisModal from "./AiAnalysisModal";
import QuickFilterModal from "./QuickFilterModal";
import CallSection from "./CallSection";
import { buildGhlGridColumns, buildKixieGridColumns } from "./gridColumns";

// ---------- Main Component ----------
export default function CallsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showQuickFilterModal, setShowQuickFilterModal] = useState(false);
  const [kixieAttendees, setKixieAttendees] = useState([]);
  const [ghlAttendees, setGhlAttendees] = useState([]);

  // AI Analysis state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedCallSource, setSelectedCallSource] = useState(null);
  const [generating, setGenerating] = useState({});

  // Prompt modal state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [pendingCallId, setPendingCallId] = useState(null);
  const [pendingSource, setPendingSource] = useState(null);
  const [pendingOptions, setPendingOptions] = useState({}); // regenerate flags
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  const [aiPrompt, setAiPrompt] = useState("");

  // ---------- Pagination state ----------
  const PAGE_LIMIT = 50;
  const defaultPagination = { page: 1, limit: PAGE_LIMIT, total: 0 };
  const [kixiePagination, setKixiePagination] = useState({
    outgoing: { ...defaultPagination },
    incoming: { ...defaultPagination },
  });
  const [ghlPagination, setGhlPagination] = useState({
    outbound: { ...defaultPagination },
    inbound: { ...defaultPagination },
  });
  // Per-table loading flags so paginating one table doesn't spin the whole page
  const [tableLoading, setTableLoading] = useState({
    "kixie-outgoing": false,
    "kixie-incoming": false,
    "ghl-outbound": false,
    "ghl-inbound": false,
  });

  // ---------- Date & Filter helpers ----------
  const getTodayUTCFilter = () => {
    const now = new Date();
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    };
  };

  // Duration threshold defaults used to split stats into "below" / "above" buckets
  const DEFAULT_GHL_THRESHOLD = 60;
  const DEFAULT_KIXIE_THRESHOLD = 30;

  const [quickFilter, setQuickFilter] = useState(() => {
    const defaultFilter = getTodayUTCFilter();
    return {
      from: searchParams.get("from") || defaultFilter.from,
      to: searchParams.get("to") || defaultFilter.to,
      agent: searchParams.get("agent") || "",
      kixieAgent: searchParams.get("kixieAgent") || "",
      ghlThreshold: searchParams.get("ghlThreshold") || DEFAULT_GHL_THRESHOLD,
      kixieThreshold: searchParams.get("kixieThreshold") || DEFAULT_KIXIE_THRESHOLD,
    };
  });

  const defaultCallState = {
    calls: [],
    total_calls: 0,
    total_duration: 0,
    total_amount: 0,
    calls_with_duration: 0,
    calls_without_duration: 0,
    avg_duration: 0,
    duration_threshold: 0,
    calls_below_threshold: 0,
    calls_above_threshold: 0,
    duration_below_threshold_sum: 0,
    duration_above_threshold_sum: 0,
    calls_by_attendee: [],
    calls_by_status: [],
  };

  const [kixieCalls, setKixieCalls] = useState({
    outgoing: defaultCallState,
    incoming: defaultCallState,
  });

  const [ghlCalls, setGhlCalls] = useState({
    outbound: defaultCallState,
    inbound: defaultCallState,
  });

  const [exporting, setExporting] = useState(false);

  const buildDateFilters = (filters, mode = "ghl") => {
    let fromDate = null;
    let toDate = null;

    if (filters?.from) {
      if (mode === "ghl") {
        // Shift start back to 22:00 UTC on the PREVIOUS day
        const base = new Date(`${filters.from}T00:00:00.000Z`);
        base.setUTCDate(base.getUTCDate() - 1);
        base.setUTCHours(22, 0, 0, 0);
        fromDate = base;
      } else {
        fromDate = new Date(`${filters.from}T00:00:00.000Z`);
      }
    }

    if (filters?.to) {
      if (mode === "ghl") {
        // End at 21:59:59.999 UTC on the SELECTED day
        const base = new Date(`${filters.to}T00:00:00.000Z`);
        base.setUTCHours(21, 59, 59, 999);
        toDate = base;
      } else {
        toDate = new Date(`${filters.to}T23:59:59.999Z`);
      }
    }

    return {
      from_date: fromDate?.toISOString(),
      to_date: toDate?.toISOString(),
      agent: mode === "ghl" ? filters?.agent || undefined : filters.kixieAgent || undefined,
      duration_threshold: mode === "ghl" ? filters?.ghlThreshold || undefined : filters?.kixieThreshold || undefined,
    };
  };

  const mapCallResponse = (data) => ({
    calls: data?.calls || [],
    total_calls: data?.total_calls || 0,
    total_duration: data?.total_duration || 0,
    total_amount: data?.total_amount || 0,
    calls_with_duration: data?.calls_with_duration || 0,
    calls_without_duration: data?.calls_without_duration || 0,
    avg_duration: data?.avg_duration || 0,
    duration_threshold: data?.duration_threshold || 0,
    calls_below_threshold: data?.calls_below_threshold || 0,
    calls_above_threshold: data?.calls_above_threshold || 0,
    duration_below_threshold_sum: data?.duration_below_threshold_sum || 0,
    duration_above_threshold_sum: data?.duration_above_threshold_sum || 0,
    calls_by_attendee: data?.calls_by_attendee || [],
    calls_by_status: data?.calls_by_status || [],
  });

  // ---------- API fetch functions ----------

  const fetchAiAnalysisPrompt = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/ai-prompt?prompt_type=-7", {});
      if (data?.data?.length) {
        setAiPrompt(data.data[0].prompt_text);
      }
    } catch (error) {
      console.error("error getting AI prompt.", error);
    }
  };

  const fetchKixieAttendees = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/kixie/attendees", {});
      if (data?.success) {
        setKixieAttendees(data.attendees || []);
      }
    } catch (error) {
      console.error("error getting kixie attendees.", error);
    }
  };

  const fetchGhlAttendees = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/ghl/attendees", {});
      if (data?.success) {
        setGhlAttendees(data.attendees || []);
      }
    } catch (error) {
      console.error("error getting ghl attendees.", error);
    }
  };

  const fetchKixieCalls = async ({ type = "outgoing", filters = quickFilter, page = 1, limit = PAGE_LIMIT, loader = false }) => {
    try {
      if (loader) setLoading(true);
      setTableLoading((prev) => ({ ...prev, [`kixie-${type}`]: true }));
      const { data } = await axiosInstance.get("api/webhooks/kixie/calls", {
        params: {
          page,
          limit,
          calltype: type,
          ...buildDateFilters(filters, "kixie"),
        },
      });
      if (data?.success) {
        setKixieCalls((prev) => ({
          ...prev,
          [type]: mapCallResponse(data.data),
        }));
        setKixiePagination((prev) => ({
          ...prev,
          [type]: {
            page: data.data?.page || page,
            limit: data.data?.limit || limit,
            total: data.data?.total_calls || 0,
          },
        }));
      }
    } catch (error) {
      console.error("error getting kixie calls.", error);
    } finally {
      setLoading(false);
      setTableLoading((prev) => ({ ...prev, [`kixie-${type}`]: false }));
    }
  };

  const fetchGHLCalls = async ({ type = "outbound", filters = quickFilter, page = 1, limit = PAGE_LIMIT, loader = false }) => {
    try {
      if (loader) setLoading(true);
      setTableLoading((prev) => ({ ...prev, [`ghl-${type}`]: true }));
      const { data } = await axiosInstance.get("api/webhooks/ghl/calls", {
        params: {
          page,
          limit,
          direction: type,
          ...buildDateFilters(filters),
        },
      });
      if (data?.success) {
        setGhlCalls((prev) => ({
          ...prev,
          [type]: mapCallResponse(data.data),
        }));
        setGhlPagination((prev) => ({
          ...prev,
          [type]: {
            page: data.data?.page || page,
            limit: data.data?.limit || limit,
            total: data.data?.total_calls || 0,
          },
        }));
      }
    } catch (error) {
      console.error("error getting ghl calls.", error);
    } finally {
      setLoading(false);
      setTableLoading((prev) => ({ ...prev, [`ghl-${type}`]: false }));
    }
  };

  useEffect(() => {
    if (!searchParams.get("from") && !searchParams.get("to")) {
      const defaultFilter = getTodayUTCFilter();
      setSearchParams(
        {
          from: defaultFilter.from,
          to: defaultFilter.to,
          agent: searchParams.get("agent") || "",
          kixieAgent: searchParams.get("kixieAgent") || "",
          ghlThreshold: searchParams.get("ghlThreshold") || DEFAULT_GHL_THRESHOLD,
          kixieThreshold: searchParams.get("kixieThreshold") || DEFAULT_KIXIE_THRESHOLD,
        },
        { replace: true }, // don't add an extra history entry
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAiAnalysisPrompt();
    fetchKixieAttendees();
    fetchGhlAttendees();
  }, []);

  useEffect(() => {
    const filters = {
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
      agent: searchParams.get("agent") || "",
      kixieAgent: searchParams.get("kixieAgent") || "",
      ghlThreshold: searchParams.get("ghlThreshold") || DEFAULT_GHL_THRESHOLD,
      kixieThreshold: searchParams.get("kixieThreshold") || DEFAULT_KIXIE_THRESHOLD,
    };
    setQuickFilter(filters);
    // Reset all tables back to page 1 whenever filters change
    setKixiePagination({ outgoing: { ...defaultPagination }, incoming: { ...defaultPagination } });
    setGhlPagination({ outbound: { ...defaultPagination }, inbound: { ...defaultPagination } });
    fetchKixieCalls({ type: "outgoing", filters, page: 1, loader: true });
    fetchKixieCalls({ type: "incoming", filters, page: 1, loader: true });
    fetchGHLCalls({ type: "outbound", filters, page: 1, loader: true });
    fetchGHLCalls({ type: "inbound", filters, page: 1, loader: true }); // eslint-disable-next-line
  }, [searchParams]);

  const handleApplyFilter = async () => {
    const params = {
      from: quickFilter.from,
      to: quickFilter.to,
      agent: quickFilter.agent,
      kixieAgent: quickFilter.kixieAgent,
      ghlThreshold: quickFilter.ghlThreshold || DEFAULT_GHL_THRESHOLD,
      kixieThreshold: quickFilter.kixieThreshold || DEFAULT_KIXIE_THRESHOLD,
    };
    setShowQuickFilterModal(false);
    setSearchParams(params);
    // Note: the searchParams effect above will re-fetch page 1 for all tables
  };

  // ---------- Pagination handlers (MUI DataGrid model is 0-indexed) ----------
  const handleKixiePaginationModelChange = (type, model) => {
    fetchKixieCalls({ type, filters: quickFilter, page: model.page + 1, limit: model.pageSize });
  };

  const handleGhlPaginationModelChange = (type, model) => {
    fetchGHLCalls({ type, filters: quickFilter, page: model.page + 1, limit: model.pageSize });
  };

  const formatDuration = (seconds) => {
    const totalSeconds = parseInt(seconds || 0, 10);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    if (mins > 0) {
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${secs}s`;
  };

  // ---------- AI Analysis Functions ----------
  const enhanceCall = async (callId, source, prompt, options = {}) => {
    try {
      setGenerating((prev) => ({ ...prev, [callId]: true }));
      const payload = {
        call_id: callId,
        source: source,
        prompt: prompt,
        ...options,
      };
      const { data } = await axiosInstance.post("api/webhooks/call-enhance", payload);

      // Refresh the table data (optional, but keeps lists in sync), staying on the current page
      await refreshAllData();

      if (data?.success) {
        toast.success("Analysis completed successfully.");
        return data; // contains transcript, enhanced, etc.
      } else {
        toast.error(data?.message || "Failed to generate analysis.");
        return null;
      }
    } catch (error) {
      console.error("Enhance error:", error);
      toast.error(error?.response?.data?.message || "Error generating analysis.");
      return null;
    } finally {
      setGenerating((prev) => ({ ...prev, [callId]: false }));
    }
  };

  const refreshAllData = async () => {
    const filters = quickFilter;
    await Promise.all([fetchKixieCalls({ type: "outgoing", filters, page: kixiePagination.outgoing.page }), fetchKixieCalls({ type: "incoming", filters, page: kixiePagination.incoming.page }), fetchGHLCalls({ type: "outbound", filters, page: ghlPagination.outbound.page }), fetchGHLCalls({ type: "inbound", filters, page: ghlPagination.inbound.page })]);
  };

  const openAIModal = (call, source) => {
    setSelectedCall(call);
    setSelectedCallSource(source);
    setAiModalOpen(true);
  };

  const closeAIModal = () => {
    setAiModalOpen(false);
    setSelectedCall(null);
    setSelectedCallSource(null);
  };

  // Open prompt modal with optional regeneration flags
  const openPromptModal = (callId, source, options = {}) => {
    setPendingCallId(callId);
    setPendingSource(source);
    setPendingOptions(options);
    setShowPromptModal(true);
  };

  const handleGenerateWithPrompt = async (customPrompt) => {
    setIsGeneratingPrompt(true);
    setShowPromptModal(false);

    const result = await enhanceCall(pendingCallId, pendingSource, customPrompt, pendingOptions);

    if (result) {
      setSelectedCall((prev) => ({
        ...prev,
        ai_transcript: result.transcript,
        ai_analized_transcript: result.analizedTranscript,
        ai_enhanced_response: result.enhanced,
        ai_status: "completed",
        ai_error_message: null,
      }));
    }

    setIsGeneratingPrompt(false);
    setPendingCallId(null);
    setPendingSource(null);
    setPendingOptions({});
  };

  // Handlers for regeneration buttons in the AI modal
  const handleRegenerateSTTandAI = (call, source) => {
    openPromptModal(call.id, source, { regenerate_stt: true, regenerate_ai_enhance: true });
  };

  const handleRegenerateAIOnly = (call, source) => {
    openPromptModal(call.id, source, { regenerate_ai_enhance: true, regenerate_stt: false });
  };

  // ---------- Export ----------
  const handleExport = async () => {
    try {
      setExporting(true);

      const [kixieOutgoingRes, kixieIncomingRes, ghlOutboundRes, ghlInboundRes] = await Promise.all([
        axiosInstance.get("api/webhooks/kixie/calls", {
          params: {
            fetch_all: true,
            calltype: "outgoing",
            ...buildDateFilters(quickFilter, "kixie"),
          },
        }),
        axiosInstance.get("api/webhooks/kixie/calls", {
          params: {
            fetch_all: true,
            calltype: "incoming",
            ...buildDateFilters(quickFilter, "kixie"),
          },
        }),
        axiosInstance.get("api/webhooks/ghl/calls", {
          params: {
            fetch_all: true,
            direction: "outbound",
            ...buildDateFilters(quickFilter, "ghl"),
          },
        }),
        axiosInstance.get("api/webhooks/ghl/calls", {
          params: {
            fetch_all: true,
            direction: "inbound",
            ...buildDateFilters(quickFilter, "ghl"),
          },
        }),
      ]);

      const kixieOutgoing = kixieOutgoingRes.data.data;
      const kixieIncoming = kixieIncomingRes.data.data;
      const ghlOutbound = ghlOutboundRes.data.data;
      const ghlInbound = ghlInboundRes.data.data;

      const autoFitColumns = (sheet, data = []) => {
        if (!data.length) return;
        const colWidths = Object.keys(data[0]).map((key) => {
          const maxLength = Math.max(key.length, ...data.map((row) => (row[key] !== null && row[key] !== undefined ? String(row[key]).length : 0)));
          return { wch: Math.min(maxLength + 4, 200) };
        });
        sheet["!cols"] = colWidths;
      };

      const createSheet = (data) => {
        const sheet = XLSX.utils.json_to_sheet(data || []);
        autoFitColumns(sheet, data);
        return sheet;
      };

      const formatAttendees = (attendees = []) => {
        return attendees.map((item) => ({
          "Agent Name": item.caller_number || item.call_from || item.calleridName,
          "Total Calls": item.total_calls,
          "Total Duration": item.total_duration,
        }));
      };

      const formatKixieCalls = (calls = []) => {
        return calls.map((call) => ({
          "Call ID": call.callid,
          "Call Type": call.calltype || call.kixieCallDetails?.calltype,
          "Call Status": call.kixieCallDetails?.callstatus,
          Disposition: call.disposition,
          "From Number": call.kixieCallDetails?.fromnumber,
          "To Number": call.kixieCallDetails?.tonumber,
          "Agent Name": call.kixieCallDetails?.calleridName,
          "Call Date": call.kixieCallDetails?.calldate,
          "Duration (sec)": call.kixieCallDetails?.duration,
          "First Name": call.kixiePowerlistContactDetails?.firstName,
          "Last Name": call.kixiePowerlistContactDetails?.lastName,
          Company: call.kixiePowerlistContactDetails?.companyName,
          "Last Dial Outcome": call.kixiePowerlistContactDetails?.lastDialOutcome,
          "Recording URL": call.kixieCallDetails?.recordingurl,
        }));
      };

      const formatGHLCalls = (calls = []) => {
        return calls.map((call) => ({
          "Full Name": call.fullName,
          Phone: call.phone,
          Direction: call.direction,
          From: call.call_from,
          To: call.call_to,
          "Agent Name": call.user_name,
          "Duration (sec)": call.call_duration,
          "Start Time": call.call_start_time,
          "End Time": call.call_end_time,
          Status: call.call_status,
          Disposition: call.call_disposition,
          "Recording URL": call.recording_url,
        }));
      };

      // KIXIE OUTBOUND
      const kixieOutCallsSheet = createSheet(formatKixieCalls(kixieOutgoing.calls));
      const kixieOutAttendeeSheet = createSheet(formatAttendees(kixieOutgoing.calls_by_attendee));
      const kixieOutSummarySheet = createSheet([
        {
          "Total Calls": kixieOutgoing.total_calls,
          "Total Duration In Seconds": kixieOutgoing.total_duration,
          "Total Amount": kixieOutgoing.total_amount,
          "Average Duration In Seconds": kixieOutgoing.avg_duration,
        },
      ]);

      const kixieInCallsSheet = createSheet(formatKixieCalls(kixieIncoming.calls));
      const kixieInAttendeeSheet = createSheet(formatAttendees(kixieIncoming.calls_by_attendee));
      const kixieInSummarySheet = createSheet([
        {
          "Total Calls": kixieIncoming.total_calls,
          "Total Duration In Seconds": kixieIncoming.total_duration,
          "Total Amount": kixieIncoming.total_amount,
          "Average Duration In Seconds": kixieIncoming.avg_duration,
        },
      ]);

      const ghlOutCallsSheet = createSheet(formatGHLCalls(ghlOutbound.calls));
      const ghlOutAttendeeSheet = createSheet(formatAttendees(ghlOutbound.calls_by_attendee));
      const ghlOutSummarySheet = createSheet([
        {
          "Total Calls": ghlOutbound.total_calls,
          "Total Duration In Seconds": ghlOutbound.total_duration,
          "Average Duration In Seconds": ghlOutbound.avg_duration,
        },
      ]);

      const ghlInCallsSheet = createSheet(formatGHLCalls(ghlInbound.calls));
      const ghlInAttendeeSheet = createSheet(formatAttendees(ghlInbound.calls_by_attendee));
      const ghlInSummarySheet = createSheet([
        {
          "Total Calls": ghlInbound.total_calls,
          "Total Duration In Seconds": ghlInbound.total_duration,
          "Average Duration In Seconds": ghlInbound.avg_duration,
        },
      ]);

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, kixieOutCallsSheet, "Kixie Out Calls");
      XLSX.utils.book_append_sheet(wb, kixieOutAttendeeSheet, "Kixie Out Attendees");
      XLSX.utils.book_append_sheet(wb, kixieOutSummarySheet, "Kixie Out Summary");

      XLSX.utils.book_append_sheet(wb, kixieInCallsSheet, "Kixie In Calls");
      XLSX.utils.book_append_sheet(wb, kixieInAttendeeSheet, "Kixie In Attendees");
      XLSX.utils.book_append_sheet(wb, kixieInSummarySheet, "Kixie In Summary");

      XLSX.utils.book_append_sheet(wb, ghlOutCallsSheet, "GHL Out Calls");
      XLSX.utils.book_append_sheet(wb, ghlOutAttendeeSheet, "GHL Out Attendees");
      XLSX.utils.book_append_sheet(wb, ghlOutSummarySheet, "GHL Out Summary");

      XLSX.utils.book_append_sheet(wb, ghlInCallsSheet, "GHL In Calls");
      XLSX.utils.book_append_sheet(wb, ghlInAttendeeSheet, "GHL In Attendees");
      XLSX.utils.book_append_sheet(wb, ghlInSummarySheet, "GHL In Summary");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: "application/octet-stream" });
      const formatFileDate = (date) => (date ? date.replaceAll("-", "_") : null);
      const fromDate = formatFileDate(quickFilter?.from) || "today_start";
      const toDate = formatFileDate(quickFilter?.to) || "today_end";
      saveAs(file, `calls_export_${fromDate}_to_${toDate}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  // Applies an edited threshold from a section's inline pill and re-fetches page 1 for that source
  const handleThresholdChange = (key, newValue) => {
    const params = {
      from: quickFilter.from,
      to: quickFilter.to,
      agent: quickFilter.agent,
      kixieAgent: quickFilter.kixieAgent,
      ghlThreshold: key === "ghlThreshold" ? newValue : quickFilter.ghlThreshold || DEFAULT_GHL_THRESHOLD,
      kixieThreshold: key === "kixieThreshold" ? newValue : quickFilter.kixieThreshold || DEFAULT_KIXIE_THRESHOLD,
    };
    setSearchParams(params);
    // Note: the searchParams effect above will re-fetch page 1 for all tables
  };

  // ---------- Table column configs (built once per render, memo-free is fine here) ----------
  const ghlColumns = buildGhlGridColumns({ generating, onView: openAIModal, onGenerate: openPromptModal });
  const kixieColumns = buildKixieGridColumns({ generating, onView: openAIModal, onGenerate: openPromptModal });

  // ---------- Section configs: everything that differs between the 4 tables lives here ----------
  const sections = [
    {
      id: "ghl-inbound",
      sectionTitle: "GHL Incoming Calls",
      durationTitle: "Incoming Calls Duration",
      avgDurationTitle: "Incoming Average Call Duration",
      // attendeeChartTitle: "Total Calls Received by Call Attendee",
      attendeeLabelKey: "caller_number",
      statusChartTitle: "Total Calls Received by Call Status",
      tableTitle: "Incoming Calls GHL",
      data: ghlCalls.inbound,
      columns: ghlColumns,
      pagination: ghlPagination.inbound,
      onPaginationModelChange: (model) => handleGhlPaginationModelChange("inbound", model),
      tableLoadingKey: "ghl-inbound",
      exportFileName: "ghl_incoming_calls",
      onThresholdChange: (v) => handleThresholdChange("ghlThreshold", v),
    },
    {
      id: "ghl-outbound",
      sectionTitle: "GHL Outgoing Calls",
      durationTitle: "Outgoing Calls Duration",
      avgDurationTitle: "Outgoing Average Call Duration",
      attendeeChartTitle: "Total Calls by Call Attendee",
      attendeeLabelKey: "caller_number",
      statusChartTitle: "Total Calls by Call Status",
      tableTitle: "Outgoing Calls",
      data: ghlCalls.outbound,
      columns: ghlColumns,
      pagination: ghlPagination.outbound,
      onPaginationModelChange: (model) => handleGhlPaginationModelChange("outbound", model),
      tableLoadingKey: "ghl-outbound",
      exportFileName: "ghl_outgoing_calls",
      onThresholdChange: (v) => handleThresholdChange("ghlThreshold", v),
    },
    // {
    //   id: "kixie-incoming",
    //   sectionTitle: "Kixie Incoming Calls",
    //   durationTitle: "Incoming Calls Duration",
    //   avgDurationTitle: "Incoming Average Call Duration",
    //   attendeeChartTitle: "Total Calls by Call Attendee",
    //   attendeeLabelKey: "calleridName",
    //   statusChartTitle: "Total Calls by Call Status",
    //   tableTitle: "Incoming Calls",
    //   data: kixieCalls.incoming,
    //   columns: kixieColumns,
    //   pagination: kixiePagination.incoming,
    //   onPaginationModelChange: (model) => handleKixiePaginationModelChange("incoming", model),
    //   tableLoadingKey: "kixie-incoming",
    //   exportFileName: "kixie_incoming_calls",
    //   onThresholdChange: (v) => handleThresholdChange("kixieThreshold", v),
    // },
    {
      id: "kixie-outgoing",
      sectionTitle: "Kixie Outgoing Calls",
      durationTitle: "Outgoing Calls Duration",
      avgDurationTitle: "Outgoing Average Call Duration",
      attendeeChartTitle: "Total Calls by Call Attendee",
      attendeeLabelKey: "calleridName",
      statusChartTitle: "Total Calls by Call Status",
      tableTitle: "Outgoing Calls",
      data: kixieCalls.outgoing,
      columns: kixieColumns,
      pagination: kixiePagination.outgoing,
      onPaginationModelChange: (model) => handleKixiePaginationModelChange("outgoing", model),
      tableLoadingKey: "kixie-outgoing",
      exportFileName: "kixie_outgoing_calls",
      onThresholdChange: (v) => handleThresholdChange("kixieThreshold", v),
    },
  ];

  // ---------- Main Render ----------
  return (
    <div className="min-h-screen bg-gray-100 p-1 sm:p-6 font-sans">
      {/* Header with filters and export */}
      <div className="max-w-[1600px] mx-auto flex items-center justify-between w-full">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <button onClick={() => setShowQuickFilterModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all">
              <Plus size={15} />
              <span>Quick Filters</span>
            </button>
            {(quickFilter.from || quickFilter.to || quickFilter.agent) && (
              <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                {quickFilter.from || "----"} - {quickFilter.to || "----"}
                {quickFilter.agent ? ` | Agent: ${quickFilter.agent}` : ""}
                {quickFilter.kixieAgent ? ` | Kixie Agent: ${quickFilter.kixieAgent}` : ""}
                {` | GHL Threshold: ${quickFilter.ghlThreshold || DEFAULT_GHL_THRESHOLD}s`}
                {` | Kixie Threshold: ${quickFilter.kixieThreshold || DEFAULT_KIXIE_THRESHOLD}s`}
              </div>
            )}
          </div>

          <QuickFilterModal
            isOpen={showQuickFilterModal}
            filter={quickFilter}
            ghlAttendees={ghlAttendees}
            kixieAttendees={kixieAttendees}
            onChange={setQuickFilter}
            onClose={() => setShowQuickFilterModal(false)}
            onClear={() => {
              setQuickFilter({ from: "", to: "", agent: "", kixieAgent: "", ghlThreshold: DEFAULT_GHL_THRESHOLD, kixieThreshold: DEFAULT_KIXIE_THRESHOLD });
              setShowQuickFilterModal(false);
            }}
            onApply={handleApplyFilter}
          />
        </div>
        <div>
          <button onClick={handleExport} disabled={exporting} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 disabled:bg-sky-200 disabled:text-black disabled:cursor-wait ease-in-out duration-200 transition-all">
            <Download size={15} />
            <span>{exporting ? "Exporting..." : "Export Data"}</span>
          </button>
        </div>
      </div>

      {sections.map((section) => (
        <CallSection key={section.id} sectionTitle={section.sectionTitle} durationTitle={section.durationTitle} avgDurationTitle={section.avgDurationTitle} attendeeChartTitle={section.attendeeChartTitle} attendeeLabelKey={section.attendeeLabelKey} statusChartTitle={section.statusChartTitle} tableTitle={section.tableTitle} data={section.data} columns={section.columns} pagination={section.pagination} onPaginationModelChange={section.onPaginationModelChange} tableLoadingKey={section.tableLoadingKey} tableLoading={tableLoading} chartLoading={loading} formatDuration={formatDuration} exportFileName={section.exportFileName} onThresholdChange={section.onThresholdChange} />
      ))}

      {/* ========== AI Analysis Modal ========== */}
      <AiAnalysisModal isOpen={aiModalOpen} onClose={closeAIModal} call={selectedCall} source={selectedCallSource} onRegenerateSTTandAI={handleRegenerateSTTandAI} onRegenerateAIOnly={handleRegenerateAIOnly} generating={generating} />

      {/* ========== Prompt Editor Modal ========== */}
      <PromptModal isOpen={showPromptModal} onClose={() => setShowPromptModal(false)} onGenerate={handleGenerateWithPrompt} initialPrompt={aiPrompt} generating={isGeneratingPrompt} />
    </div>
  );
}
